import React, { useMemo } from 'react';
import * as THREE from 'three';
import { BattlefieldTerrain } from '@tankworld/simulation';
import { GAME_CONSTANTS } from '@tankworld/shared';

interface TerrainMeshProps {
  terrain: BattlefieldTerrain;
}

export const TerrainMesh: React.FC<TerrainMeshProps> = ({ terrain }) => {
  const geometry = useMemo(() => {
    const size = GAME_CONSTANTS.ARENA_SIZE;
    const segments = 120;
    const geo = new THREE.PlaneGeometry(size, size, segments, segments);
    geo.rotateX(-Math.PI / 2); // Lay flat on XZ plane

    const posAttr = geo.attributes.position;
    const count = posAttr.count;
    const colors = new Float32Array(count * 3);

    // Color palettes
    const grassColor = new THREE.Color('#384627'); // Tactical olive green
    const dirtColor = new THREE.Color('#4a4030');  // Earthy soil/mud
    const rockColor = new THREE.Color('#555555');  // Slate grey rock
    const tempColor = new THREE.Color();

    for (let i = 0; i < count; i++) {
      const x = posAttr.getX(i);
      const z = posAttr.getZ(i);
      const y = terrain.getHeight(x, z);
      posAttr.setY(i, y);

      // Color based on slope and height
      const normal = terrain.getNormal(x, z);
      const slope = 1.0 - normal.y; // 0 for flat, >0 for steep

      if (slope > 0.25) {
        tempColor.lerpColors(dirtColor, rockColor, Math.min(1, (slope - 0.25) * 2.5));
      } else {
        const heightFactor = Math.max(0, Math.min(1, (y + 5) / 15));
        tempColor.lerpColors(dirtColor, grassColor, 0.7 - heightFactor * 0.3);
      }

      colors[i * 3] = tempColor.r;
      colors[i * 3 + 1] = tempColor.g;
      colors[i * 3 + 2] = tempColor.b;
    }

    geo.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geo.computeVertexNormals();
    return geo;
  }, [terrain]);

  return (
    <mesh geometry={geometry} receiveShadow>
      <meshStandardMaterial
        vertexColors
        roughness={0.88}
        metalness={0.08}
        flatShading={false}
      />
    </mesh>
  );
};
